import React, { useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { theme } from 'styled-tools'
import Headline from '../../typography/headline'
import features from '../../features/'
import tracker from '../../../../utils/tracker'
import { mediaMax } from '../../../../styles/media'
import useTranslation from '../../../../utils/translation'

import ChangeLocation from './ChangeLocation'
import StoresTabButton from './StoresTabButton'
import { useCms } from '../../cms/CmsContext'
import TemporarilyClosed from '../store-list/components/store-item/TemporarilyClosed'
import PostalCodeSelector from '../postalCodeSelector/PostalCodeSelector'
import { useDeviceLocation } from '../../../hooks/location/useDeviceLocation'

const StyledHeader = styled.div`
  font-size: 20px;
  font-size: ${theme('typography.paragraph.fontSizeLead')};
  font-size: ${theme('storesHeader.header.fontSize')};
  line-height: 21px;
  line-height: ${theme('typography.paragraph.lineHeightLead')};
  font-weight: ${theme('typography.paragraph.fontWeightLead')};
  margin-bottom: 20px;
  font-family: ${theme('typography.paragraph.fontFamily')};
  color: ${theme('colors.darkGrey')};
  min-height: 22px;

  ${mediaMax(
    'sm',
    css`
      font-size: 16px;
    `
  )};
`

const HeaderContainer = styled.div`
  display: flex;
  margin-top: 25px;
  margin-bottom: 14px;

  flex-direction: column;

  ${(props) => props.theme.media.min.sm`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `}
`

const StyledHeadlineDesktop = styled(Headline)`
  display: none;
  ${(p) => p.theme.media.min.sm`
    display: block;
  `}
  text-transform:inherit;
  text-transform: ${theme('stores.headers.textTransform')};
  margin: 0;
  font-weight: ${theme('stores.headers.fontWeight')};
  line-height: ${theme('stores.headers.lineHeight')};
`

const StyledHeadlineMobile = styled(Headline)`
  ${(p) => p.theme.media.min.sm`
    display: none;
  `}
  text-transform:inherit;
  text-transform: ${theme('stores.headers.textTransform')};
  margin: 0;
`

const TabContainer = styled.dl.attrs({ role: 'presentation' })`
  display: flex;
  flex: 0 0 auto;
  margin: 0;
  margin-bottom: 10px;
  justify-content: flex-start;

  ${(p) => p.theme.media.min.md`
    justify-content: flex-end;
  `}
`

const ChangeLocationContainer = styled.div`
  margin-bottom: 10px;
`

const Message = styled.p`
  font-size: 16px;
`

const trackTab = (tab) => {
  tracker.trackClick('BADA 2.0 change store list', tab)
}

/**
 * Gets the `StoresHeader` component markup.
 * @param {{
 *   pageContent: any,
 *   currentTab: "near" | "all",
 *   changeTab: (id: string) => void),
 *   selectedAddress: {
 *     postalCode: string,
 *     addressLine1: string,
 *     addressLine2: string,
 *     addressLine3: string,
 *     city: string,
 *     countryName: string,
 *   },
 *   selectedCity: string,
 *   onAddressChange: (address: any) => void),
 *   toggleAddressChange: boolean,
 *   toggleAddress: () => void),
 *   hasSelectedAddress: boolean,
 * }} props The component properties.
 */
const StoresHeader = (props) => {
  const {
    pageContent: { storeSelectionHeadingDesktop, storeSelectionHeadingMobile },
  } = useCms()
  const t = useTranslation('app')

  const deviceLocation = useDeviceLocation(
    !props.selectedAddress?.latitude && !props.selectedAddress?.city
  )
  useEffect(() => {
    if (deviceLocation) {
      props.onChange({ selectedAddress: { ...deviceLocation, device: true } })
    }
  }, [deviceLocation])

  const useManualLocationInput = useMemo(
    () =>
      (!props.selectedAddress && !props.fallbackAddress) ||
      features.useManualLocationInput,
    [props.selectedAddress, props.fallbackAddress]
  )

  return (
    <>
      {!!props.selectedStoreUnavailable && (
        <TemporarilyClosed>
          <Message>{t('store-list.store-unavailable')}</Message>
        </TemporarilyClosed>
      )}
      <HeaderContainer>
        <div>
          {storeSelectionHeadingDesktop && (
            <StyledHeadlineDesktop>
              {storeSelectionHeadingDesktop}
            </StyledHeadlineDesktop>
          )}
          {storeSelectionHeadingMobile && (
            <StyledHeadlineMobile>
              {storeSelectionHeadingMobile}
            </StyledHeadlineMobile>
          )}
        </div>
      </HeaderContainer>
      <ChangeLocationContainer>
        {props.currentTab === 'near' && useManualLocationInput && (
          <PostalCodeSelector
            onChange={props.onAddressChange}
            buttonText={t('store-selection.address-lookup.button')}
            error={t('store-selection.address-lookup.error')}
            label={t('store-selection.address-lookup.label')}
            placeholder={t('store-selection.address-lookup.placeholder')}
          />
        )}
        {props.currentTab === 'near' && !useManualLocationInput && (
          <ChangeLocation
            selectedCity={props.selectedCity}
            selectedAddress={props.selectedAddress}
            onAddressChange={props.onAddressChange}
            toggleAddressChange={props.toggleAddressChange}
            toggleAddress={props.toggleAddress}
          />
        )}
        {props.currentTab === 'all' && (
          <StyledHeader>{t('store-selection.tab.all-header')}</StyledHeader>
        )}
      </ChangeLocationContainer>
      {props.hasSelectedAddress && (
        <TabContainer>
          <StoresTabButton
            id="near"
            currentTab={props.currentTab}
            title={t('store-selection.tab.nearest')}
            changeTab={(id) => {
              trackTab('closest')
              props.changeTab(id)
            }}
          />
          <StoresTabButton
            id="all"
            currentTab={props.currentTab}
            title={t('store-selection.tab.all')}
            changeTab={(id) => {
              trackTab('all stores')
              props.changeTab(id)
            }}
          />
        </TabContainer>
      )}
    </>
  )
}

export default StoresHeader
